import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import { useAuth } from '@/providers/AuthProvider';
import { Terminal } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';

export function LoginPage() {
 const [email, setEmail] = useState('');
 const [password, setPassword] = useState('');
 const [isLoading, setIsLoading] = useState(false);
 const { user, login } = useAuth();
 const [searchParams] = useSearchParams();
 const { toast } = useToast();
 const hasShownToast = useRef(false);

 useEffect(() => {
  if (searchParams.get('expired') === 'true' && !hasShownToast.current) {
   toast({
    title: 'Session Expired',
    description: 'Your session has expired. Please login again.',
    variant: 'destructive',
   });
   hasShownToast.current = true;
  }
 }, [searchParams, toast]);

 // Redirect if already logged in
 if (user) {
  return <Navigate to='/dashboard' replace />;
 }

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!email || !password) return;

  setIsLoading(true);
  try {
   await login(email, password);
  } catch (error) {
   // Error is handled by AuthProvider
  } finally {
   setIsLoading(false);
  }
 };

 return (
  <div className='min-h-screen w-full grid grid-cols-1 lg:grid-cols-2'>
   {/* Left Panel: Visual/System Status */}
   <div className='hidden lg:flex flex-col justify-between bg-secondary/10 border-r border-border p-12 relative overflow-hidden'>
    {/* Background Grid */}
    <div className='absolute inset-0 bg-[radial-gradient(#27272a_1px,transparent_1px)] [background-size:16px_16px] opacity-20 pointer-events-none'></div>

    <div className='relative z-10'>
     <div className='font-mono font-bold text-xl flex items-center gap-2 mb-12'>
      <div className='w-3 h-3 bg-primary animate-pulse' />
      MOCK.
     </div>
     <h1 className='text-6xl font-bold tracking-tighter leading-tight'>
      WORKSPACE
      <br />
      ACCESS
      <br />
      CONTROL
     </h1>
    </div>

    <div className='relative z-10 font-mono text-sm text-muted-foreground space-y-2'>
     <div className='flex items-center gap-2 text-primary'>
      <Terminal className='w-4 h-4' />
      <span>Secure Connection Established</span>
     </div>
     <p className='opacity-60'>// v2.0.4 Authenticating User...</p>
     <p className='opacity-60'>
      // Session ID: {Math.random().toString(36).substring(7).toUpperCase()}
     </p>
    </div>
   </div>

   {/* Right Panel: Login Form */}
   <div className='flex flex-col justify-between p-6 lg:p-12 bg-background'>
    <div className='flex justify-end'>
     <ThemeToggle />
    </div>

    <div className='max-w-md w-full mx-auto space-y-8'>
     <div className='space-y-2'>
      <h2 className='text-3xl font-bold tracking-tight'>Sign in</h2>
      <p className='text-muted-foreground font-mono text-sm'>
       Enter your credentials to access the workspace
      </p>
     </div>

     <div className='space-y-6'>
      <OAuthButtons />

      <div className='relative'>
       <div className='absolute inset-0 flex items-center'>
        <span className='w-full border-t border-border' />
       </div>
       <div className='relative flex justify-center text-xs uppercase'>
        <span className='bg-background px-2 text-muted-foreground font-mono'>
         Or continue with email
        </span>
       </div>
      </div>

      <form onSubmit={handleSubmit} className='space-y-6'>
       <div className='space-y-2'>
        <Label
         htmlFor='email'
         className='font-mono text-xs uppercase tracking-wider'
        >
         Email Address
        </Label>
        <Input
         id='email'
         type='email'
         placeholder='name@company.com'
         value={email}
         onChange={(e) => setEmail(e.target.value)}
         className='h-12 font-mono'
         required
        />
       </div>

       <div className='space-y-2'>
        <div className='flex items-center justify-between'>
         <Label
          htmlFor='password'
          className='font-mono text-xs uppercase tracking-wider'
         >
          Password
         </Label>
         <Link
          to='/forgot-password'
          className='text-xs text-primary hover:underline font-mono'
         >
          Forgot?
         </Link>
        </div>
        <Input
         id='password'
         type='password'
         value={password}
         onChange={(e) => setPassword(e.target.value)}
         className='h-12 font-mono'
         required
        />
       </div>

       <Button
        type='submit'
        className='w-full h-12 text-base font-bold dark:text-black'
        disabled={isLoading}
       >
        {isLoading ? (
         <span className='flex items-center gap-2 font-mono'>
          <div className='w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin' />
          PROCESSING...
         </span>
        ) : (
         'AUTHENTICATE'
        )}
       </Button>
      </form>

      <p className='text-center text-sm text-muted-foreground'>
       No account yet?{' '}
       <Link to='/register' className='text-primary hover:underline font-mono'>
        Register New Account
       </Link>
      </p>
     </div>
    </div>

    <div className='font-mono text-xs text-muted-foreground text-center lg:text-left pt-8'>
     Target: Production // Region: Global
    </div>
   </div>
  </div>
 );
}
